import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Download, 
  FileText, 
  Receipt,
  Filter,
  ChevronLeft, 
  ChevronRight,
  FileSpreadsheet,
  Calendar
} from 'lucide-react';
import { 
  getInvoices, 
  downloadInvoicePdf, 
  downloadFile,
  InvoiceListItem,
  InvoiceType,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS
} from '@/services/invoicesApi';

export const InvoicesList = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState<InvoiceType>('all');
  const pageSize = 10;

  useEffect(() => {
    loadInvoices();
  }, [page, typeFilter]);

  const loadInvoices = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getInvoices(token, typeFilter, page, pageSize);
      setInvoices(response.items);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (error) {
      console.error('Error loading invoices:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('n\'est pas encore disponible')) {
          toast.error('L\'API des factures n\'est pas encore disponible sur le serveur');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('Erreur lors du chargement des factures');
      }
      
      // Set empty state for better UX
      setInvoices([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId: number, invoiceNumber: string) => {
    if (!token) return;

    try {
      toast.loading('Téléchargement en cours...');
      const blob = await downloadInvoicePdf(token, invoiceId);
      downloadFile(blob, `${invoiceNumber}.pdf`);
      toast.success('Facture téléchargée avec succès');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Erreur lors du téléchargement de la facture');
      }
    }
  };

  const handleExportData = () => {
    if (invoices.length === 0) {
      toast.error('Aucune donnée à exporter');
      return;
    }

    try {
      // Create CSV content
      const headers = ['Numéro', 'Date', 'Type', 'Montant', 'Devise', 'Statut'];
      const csvContent = [
        headers.join(','),
        ...invoices.map(invoice => [
          `"${invoice.invoice_number}"`,
          invoice.date,
          INVOICE_TYPE_LABELS[invoice.type],
          invoice.amount.toFixed(2),
          invoice.currency,
          `"${invoice.status_label}"`
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      downloadFile(blob, `factures_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success('Export réalisé avec succès');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Erreur lors de l\'export des données');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (amount: number, currency: string) => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'paid':
        return 'default';
      case 'not_paid':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'invoice' ? <FileText className="w-4 h-4" /> : <Receipt className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Mes Factures
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-10 w-48" />
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Mes Factures
            {total > 0 && (
              <Badge variant="secondary" className="ml-2">
                {total}
              </Badge>
            )}
          </CardTitle>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={typeFilter} onValueChange={(value: InvoiceType) => {
              setTypeFilter(value);
              setPage(1);
            }}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="invoice">Factures uniquement</SelectItem>
                <SelectItem value="credit_note">Avoirs uniquement</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={handleExportData}
              disabled={invoices.length === 0}
              className="w-full sm:w-auto"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {invoices.length === 0 ? (
          <div className="text-center py-8">
            <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune facture trouvée</h3>
            <p className="text-muted-foreground">
              {typeFilter === 'all' 
                ? 'Vous n\'avez pas encore de factures.' 
                : `Aucune ${typeFilter === 'invoice' ? 'facture' : 'avoir'} trouvé.`
              }
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {formatDate(invoice.date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTypeIcon(invoice.type)}
                          {INVOICE_TYPE_LABELS[invoice.type]}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatPrice(invoice.amount, invoice.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={getStatusVariant(invoice.status)}
                          className="text-xs"
                        >
                          {invoice.status_label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                          className="ml-2"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {page} sur {totalPages} • {total} facture{total > 1 ? 's' : ''} au total
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};